import { useEffect, useMemo, useRef, useState } from "react";
import { startPointerDrag } from "../../lib/dragPointer";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { ToggleSwitch } from "../ui/ToggleSwitch";
import { useTesisPicker, resolveTesisId, TesisPickerField } from "../ui/TesisPickerField";
import { FormulaEditor } from "./FormulaEditor";
import { type Formula, formulaRefs } from "../../lib/formFormula";
import { TableColumnsEditor } from "./TableColumnsEditor";
import { type TableColumn, blankColumn } from "../../lib/formTable";
import { GRID_COLS, BUILDER_ROW_H, BUILDER_ROW_GAP, autoPackPositions, resolvePlacement, appendSlot, maxRow, nextFreeRow, nextFreeCol, applySpan } from "../../lib/formLayout";
import { type FieldStyle, DEFAULT_DIVIDER_STYLE, DEFAULT_CARD_STYLE, dividerStyles, cardStyles } from "../../lib/formStyle";
import { FormFillDrawer, type FieldShape } from "./FormFillDrawer";
import "../../pages/Forms.css";

/** Kararlı alan referansı (formül referansları için; kaydetmede korunur, DB id'den bağımsız). */
function makeRefKey(): string {
  try { return "r" + crypto.randomUUID().replace(/-/g, "").slice(0, 12); } catch { return "r" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4); }
}
/** Bir formülden silinen/değişen refKey referanslarını temizle (dangling ref → kaydetmede sunucu reddini önle). */
function stripRef(formula: Formula, ref: string): Formula {
  const steps = formula.steps.filter((s) => !(s.operand.kind === "field" && s.operand.ref === ref));
  let start = formula.start;
  if (start.kind === "field" && start.ref === ref) start = { kind: "const", value: 0 };
  return { start, steps };
}

/** Builder alan tipleri + meta (palet ikonu/etiketi, davranış bayrakları). */
type FieldType = "text" | "number" | "textarea" | "select" | "boolean" | "date" | "time" | "heading" | "note" | "table" | "divider" | "card";
const TYPE_META: Record<FieldType, { icon: string; label: string; value: boolean; options?: boolean; number?: boolean; table?: boolean; divider?: boolean; card?: boolean }> = {
  text: { icon: "type", label: "Metin", value: true },
  number: { icon: "hash", label: "Sayı", value: true, number: true },
  textarea: { icon: "alignLeft", label: "Uzun Metin", value: true },
  select: { icon: "list", label: "Seçim", value: true, options: true },
  boolean: { icon: "toggleLeft", label: "Evet/Hayır", value: true },
  date: { icon: "calendar", label: "Tarih", value: true },
  time: { icon: "clock", label: "Saat", value: true },
  table: { icon: "table", label: "Tablo", value: false, table: true },
  divider: { icon: "minus", label: "Çizgi", value: false, divider: true },
  card: { icon: "square", label: "Kart", value: false, card: true },
  heading: { icon: "heading", label: "Başlık", value: false },
  note: { icon: "fileText", label: "Bilgi Notu", value: false },
};
const PALETTE: FieldType[] = ["text", "number", "textarea", "select", "boolean", "date", "time", "table", "divider", "card", "heading", "note"];

interface BuilderField {
  key: string; // istemci-yerel benzersiz anahtar (React key + seçim)
  refKey: string; // KARARLI referans (formüller bununla referans verir; kaydetmede korunur)
  type: FieldType;
  label: string;
  help: string;
  placeholder: string;
  required: boolean;
  options: string[];
  minVal: string; // string state → kaydetmede number|null
  maxVal: string;
  unit: string;
  colSpan: number;
  colStart: number; // serbest yerleşim: başlangıç kolonu (0-11)
  rowStart: number; // serbest yerleşim: satır (>=0)
  rowSpan: number; // yükseklik (satır) — kart/dikey-çizgi (aksi halde 1)
  formula: Formula | null; // dolu = hesaplanan (sayısal) alan
  columns: TableColumn[]; // type=table için kolon tanımları (aksi halde boş)
  style: FieldStyle | null; // divider/card stili (aksi halde null)
}

let uidCounter = 0;
const uid = () => `fld-${++uidCounter}`;

/** Builder alanını (KAYDEDİLMEMİŞ) önizleme için FieldShape'e çevir — min/max string→number|null; id=client key. */
function toPreviewShape(f: BuilderField): FieldShape {
  const num = (s: string) => { const t = s.trim(); if (t === "") return null; const n = Number(t.replace(",", ".")); return Number.isFinite(n) ? n : null; };
  return {
    id: f.key, type: f.type, label: f.label, help: f.help || null, placeholder: f.placeholder || null,
    required: f.required, options: f.options, minVal: num(f.minVal), maxVal: num(f.maxVal),
    unit: f.unit || null, colSpan: f.colSpan, refKey: f.refKey, formula: f.formula, columns: f.columns,
    colStart: f.colStart, rowStart: f.rowStart, rowSpan: f.rowSpan, style: f.style,
  };
}

function blankField(type: FieldType): BuilderField {
  const fullW = type === "heading" || type === "note" || type === "textarea" || type === "table" || type === "divider" || type === "card";
  const style = type === "divider" ? { ...DEFAULT_DIVIDER_STYLE } : type === "card" ? { ...DEFAULT_CARD_STYLE } : null;
  const label = type === "divider" ? "" : type === "card" ? "Grup" : TYPE_META[type].label;
  return { key: uid(), refKey: makeRefKey(), type, label, help: "", placeholder: "", required: false, options: type === "select" ? ["Seçenek 1"] : [], minVal: "", maxVal: "", unit: "", colSpan: type === "card" ? 12 : fullW ? 12 : 6, colStart: 0, rowStart: 0, rowSpan: type === "card" ? 3 : 1, formula: null, columns: type === "table" ? [{ ...blankColumn(), label: "Kolon 1" }] : [], style };
}

interface Props {
  open: boolean;
  templateId: string | null; // null = yeni form
  onClose: () => void;
  onSaved: (msg: string) => void;
}

export function FormBuilderDrawer({ open, templateId, onClose, onSaved }: Props) {
  const isEdit = !!templateId;
  const tesisPicker = useTesisPicker(!isEdit);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tesisValue, setTesisValue] = useState("");
  const [status, setStatus] = useState<"draft" | "published" | "archived">("draft");
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false); // sol-alt "Önizleme" → builder kapanmadan doldurma önizlemesi
  const [error, setError] = useState<string | null>(null);
  // Serbest yerleşim sürükleme: dragKey state (yalnızca .dragging sınıfı + önizleme için) + hedef hücre.
  // Izgara overlay'i HER ZAMAN mount'tur (koşullu değil): dragstart anında DOM'a düğüm eklenirse native
  // sürükleme drop'u tetiklemez; overlay sabit durup yalnızca .dragging ile pointer-events kazanır.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropCell, setDropCell] = useState<{ col: number; row: number } | null>(null);
  // Kanvas DOM ref'i: dragstart'ta `.dragging` sınıfını SENKRON ekleyip (React commit'ini beklemeden)
  // overlay'i anında pointer-events:auto yapmak için — yoksa hızlı sürüklemede overlay drop hedefi olamıyor.
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const getQ = trpc.form.template.get.useQuery({ id: templateId! }, { enabled: open && isEdit, staleTime: 0 });
  const utils = trpc.useUtils();
  const saveMut = trpc.form.template.upsert.useMutation();

  // Aç/yükle → state doldur (yeni = boş, düzenleme = get sonucu).
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!isEdit) {
      setName(""); setDescription(""); setTesisValue(""); setStatus("draft"); setFields([]); setSelectedKey(null);
      return;
    }
    if (getQ.data) {
      setName(getQ.data.name);
      setDescription(getQ.data.description ?? "");
      setStatus(getQ.data.status);
      const mapped = getQ.data.fields.map((f) => ({
        key: uid(), refKey: (f as { refKey?: string }).refKey || makeRefKey(), type: f.type as FieldType, label: f.label, help: f.help ?? "", placeholder: f.placeholder ?? "",
        required: f.required, options: f.options ?? [], minVal: f.minVal != null ? String(f.minVal) : "", maxVal: f.maxVal != null ? String(f.maxVal) : "",
        unit: f.unit ?? "", colSpan: f.colSpan, formula: ((f as { formula?: Formula | null }).formula ?? null),
        columns: ((f as { columns?: TableColumn[] | null }).columns ?? []),
        colStart: (f as { colStart?: number | null }).colStart ?? null,
        rowStart: (f as { rowStart?: number | null }).rowStart ?? null,
        rowSpan: (f as { rowSpan?: number | null }).rowSpan ?? 1,
        style: ((f as { style?: FieldStyle | null }).style ?? null),
      }));
      // Konumsuz (eski) form → sortOrder sırasıyla otomatik paketle (mevcut görünümü korur); sonraki kayıtta açık konum yazılır.
      const needsPack = mapped.some((f) => f.colStart == null || f.rowStart == null);
      setFields(needsPack
        ? autoPackPositions(mapped.map((f) => ({ ...f, colStart: 0, rowStart: 0 })))
        : (mapped as BuilderField[]));
      setSelectedKey(null);
    }
  }, [open, isEdit, getQ.data]);

  const selected = useMemo(() => fields.find((f) => f.key === selectedKey) ?? null, [fields, selectedKey]);
  // Formül operandı olabilecek alanlar: seçili alan HARİÇ tüm sayısal alanlar (refKey + etiket).
  const formulaCandidates = useMemo(
    () => fields.filter((f) => TYPE_META[f.type].number && f.key !== selectedKey).map((f) => ({ refKey: f.refKey, label: f.label.trim() || "(etiketsiz)" })),
    [fields, selectedKey],
  );

  function patch(key: string, p: Partial<BuilderField>) {
    setFields((s) => s.map((f) => (f.key === key ? { ...f, ...p } : f)));
  }
  function addField(type: FieldType) {
    const nf = blankField(type);
    setFields((s) => {
      const slot = appendSlot(s, nf.colSpan); // son satırda yer varsa oraya, yoksa alta yeni satır
      return [...s, { ...nf, ...slot }];
    });
    setSelectedKey(nf.key);
  }
  /** Var olan bir elemanı ÇOĞALT — tüm özellikleriyle (derin kopya), yeni kararlı refKey + boş konuma. */
  function duplicateField(key: string) {
    const cloneKey = uid();
    setFields((s) => {
      const src = s.find((f) => f.key === key);
      if (!src) return s;
      const slot = src.type === "card" ? { colStart: 0, rowStart: maxRow(s) + 1 } : appendSlot(s, src.colSpan);
      const clone: BuilderField = {
        ...src, key: cloneKey, refKey: makeRefKey(),
        label: src.label ? `${src.label} (kopya)` : src.label,
        colStart: slot.colStart, rowStart: slot.rowStart,
        options: [...src.options],
        columns: src.columns.map((c) => ({ ...c, options: [...c.options] })),
        formula: src.formula ? { start: { ...src.formula.start }, steps: src.formula.steps.map((st) => ({ op: st.op, operand: { ...st.operand } })) } : null,
        style: src.style ? { ...src.style } : null,
      };
      return [...s, clone];
    });
    setSelectedKey(cloneKey);
  }
  function removeField(key: string) {
    setFields((s) => {
      const gone = s.find((f) => f.key === key);
      const rest = s.filter((f) => f.key !== key);
      // Silinen alanın refKey'ine referans veren formülleri temizle (dangling ref önle).
      return gone ? rest.map((f) => (f.formula ? { ...f, formula: stripRef(f.formula, gone.refKey) } : f)) : rest;
    });
    if (selectedKey === key) setSelectedKey(null);
  }
  const clampCol = (col: number, span: number) => Math.min(GRID_COLS - span, Math.max(0, col));
  /** Alanı ızgarada (col,row) hedefine taşı. Kart = arka plan → SERBEST (çakışma çözümü yok); diğerleri çakışmasız slot. */
  function placeField(key: string, wantCol: number, wantRow: number) {
    setFields((s) => {
      const i = s.findIndex((f) => f.key === key);
      if (i < 0) return s;
      const f = s[i]!;
      const p = f.type === "card"
        ? { colStart: clampCol(wantCol, f.colSpan), rowStart: Math.max(0, wantRow) }
        : resolvePlacement(s, i, wantCol, wantRow, f.colSpan, f.rowSpan);
      if (p.colStart === f.colStart && p.rowStart === f.rowStart) return s;
      return s.map((x, idx) => (idx === i ? { ...x, ...p } : x));
    });
  }
  /** Satır taşı (yukarı/aşağı) — KOLON korunur; kartta serbest, diğerlerinde çakışmasız. */
  function move(key: string, dir: -1 | 1) {
    setFields((s) => {
      const i = s.findIndex((f) => f.key === key);
      if (i < 0) return s;
      const f = s[i]!;
      const row = f.type === "card" ? Math.max(0, f.rowStart + dir) : nextFreeRow(s, i, f.colStart, f.rowStart, dir, f.colSpan, f.rowSpan);
      return row === f.rowStart ? s : s.map((x, idx) => (idx === i ? { ...x, rowStart: row } : x));
    });
  }
  /** Kolon taşı (sola/sağa) — SATIR korunur. */
  function moveCol(key: string, dir: -1 | 1) {
    setFields((s) => {
      const i = s.findIndex((f) => f.key === key);
      if (i < 0) return s;
      const f = s[i]!;
      const col = f.type === "card" ? clampCol(f.colStart + dir, f.colSpan) : nextFreeCol(s, i, f.rowStart, f.colStart, dir, f.colSpan, f.rowSpan);
      return col === f.colStart ? s : s.map((x, idx) => (idx === i ? { ...x, colStart: col } : x));
    });
  }
  /** Yükseklik (satır) — kart/dikey-çizgi. */
  function setRowSpan(key: string, delta: number) {
    setFields((s) => s.map((f) => (f.key === key ? { ...f, rowSpan: Math.min(50, Math.max(1, f.rowSpan + delta)) } : f)));
  }
  function setRowSpanTo(key: string, v: number) {
    setFields((s) => s.map((f) => (f.key === key ? { ...f, rowSpan: Math.min(50, Math.max(1, v)) } : f)));
  }
  const patchStyle = (key: string, p: Partial<FieldStyle>) => setFields((s) => s.map((f) => (f.key === key ? { ...f, style: { ...(f.style ?? {}), ...p } } : f)));
  /** Genişlik — MUTLAK değer (slider + ± butonları TEK yol): colStart kısılır, çakışırsa değişmez. */
  function setSpanTo(key: string, next: number) {
    setFields((s) => { const i = s.findIndex((f) => f.key === key); return i < 0 ? s : applySpan(s, i, next); });
  }
  function setSpan(key: string, delta: number) {
    setFields((s) => { const i = s.findIndex((f) => f.key === key); return i < 0 ? s : applySpan(s, i, (s[i]!.colSpan) + delta); });
  }
  // Sürükle-bırak: alan ızgarada hedef hücreye SERBEST yerleşir (sıraya girmez).
  function endDrag() { canvasRef.current?.classList.remove("dragging"); setDragKey(null); setDropCell(null); }
  // Kanvas satır sayısı: en alttaki alan + boş bırakma alanı. dragKey'e BAĞLI DEĞİL → overlay hücre
  // sayısı dragstart'ta değişmez (drop'u bozan reflow/DOM churn olmaz).
  const gridRows = Math.max(5, maxRow(fields) + 3);
  const dragField = dragKey ? fields.find((f) => f.key === dragKey) ?? null : null;
  // Önizleme = alanın GERÇEKTEN yerleşeceği yer → kart serbest (clamp), diğerleri çakışmasız slot; vurgulanan hücre yalan söylemez.
  const previewPos = dropCell && dragField
    ? (dragField.type === "card"
        ? { colStart: Math.min(GRID_COLS - dragField.colSpan, Math.max(0, dropCell.col)), rowStart: Math.max(0, dropCell.row) }
        : resolvePlacement(fields, fields.findIndex((f) => f.key === dragKey), dropCell.col, dropCell.row, dragField.colSpan, dragField.rowSpan))
    : null;

  // Pointer (fare) tabanlı sürükleme — native HTML5 DnD YERİNE. Native DnD güvenilmezdi (dragstart'ta
  // overlay mount → Chrome iptali; ayrıca otomasyon aracıyla test edilemiyordu). Bu yaklaşım imlecin ekran
  // konumundan kanvasın bounding-rect'i ile ızgara hücresini hesaplar, belge-seviyesi mousemove/mouseup ile izler.
  function cellFromPoint(x: number, y: number): { col: number; row: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    if (x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
    const pad = 12, gap = BUILDER_ROW_GAP;
    const innerW = r.width - pad * 2;
    const colW = (innerW - gap * (GRID_COLS - 1)) / GRID_COLS;
    const relX = Math.max(0, Math.min(innerW, x - r.left - pad));
    const relY = Math.max(0, y - r.top - pad);
    const col = Math.max(0, Math.min(GRID_COLS - 1, Math.floor(relX / (colW + gap))));
    const row = Math.max(0, Math.min(gridRows - 1, Math.floor(relY / (BUILDER_ROW_H + gap))));
    return { col, row };
  }
  function beginDrag(e: React.MouseEvent, key: string) {
    // Sürükleme çekirdeği lib/dragPointer.ts'te ORTAK (kanban + pano ile aynı).
    // Buradaki eski kopyada `buttons === 0` kontrolü YOKTU: fare pencere dışında
    // bırakılınca mouseup gelmiyor ve sürükleme takılı kalıyordu — helper'a
    // geçmek bunu da düzeltir.
    startPointerDrag(e, {
      ignoreSelectors: [".fb-cell-acts"],           // aksiyon butonlarından sürükleme başlamaz
      dragClass: "dragging",
      dragClassTarget: canvasRef.current,
      onStart: () => { setSelectedKey(key); setDragKey(key); },
      onMove: (x, y) => setDropCell(cellFromPoint(x, y)),
      onEnd: (x, y, moved) => {
        if (!moved) return;                          // hareket yok → salt tıklama (onClick seçer)
        const cell = cellFromPoint(x, y);
        if (cell) placeField(key, cell.col, cell.row);
        endDrag();
      },
    });
  }
  // Kanvas DOM sırası = OKUMA sırası (satır→kolon) → mobil tek-sütun görünümü + tab sırası kaydedilen sortOrder ile aynı.
  const orderedFields = useMemo(() => [...fields].sort((a, b) => a.rowStart - b.rowStart || a.colStart - b.colStart), [fields]);
  const previewFields = useMemo(() => fields.map(toPreviewShape), [fields]); // önizleme = builder'ın anlık (kaydedilmemiş) durumu

  function validate(): string | null {
    if (!name.trim()) return "Form adı zorunlu.";
    for (const f of fields) {
      if (!TYPE_META[f.type].divider && !TYPE_META[f.type].card && !f.label.trim()) return `Her alanın bir etiketi olmalı (${TYPE_META[f.type].label}).`;
      if (f.type === "select" && f.options.filter((o) => o.trim()).length === 0) return `"${f.label}" seçim alanı için en az bir seçenek girin.`;
      if (f.type === "number" && !f.formula && f.minVal !== "" && f.maxVal !== "" && Number(f.minVal) > Number(f.maxVal)) return `"${f.label}" için Min değeri Max'tan büyük olamaz.`; // hesaplanan alanda min/max gizli (bayat state'i yoksay)
      if (f.type === "table") {
        if (f.columns.length === 0) return `"${f.label}" tablosu için en az bir kolon ekleyin.`;
        for (const c of f.columns) {
          if (!c.label.trim()) return `"${f.label}" tablosundaki her kolonun bir başlığı olmalı.`;
          if (c.type === "select" && c.options.filter((o) => o.trim()).length === 0) return `"${f.label}" · "${c.label}" seçim kolonu için en az bir seçenek girin.`;
        }
      }
    }
    return null;
  }

  async function save() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    try {
      const payload = {
        ...(isEdit ? { id: templateId! } : {}),
        name: name.trim(),
        description: description.trim() || null,
        ...(tesisPicker.show ? { tesisId: resolveTesisId(true, tesisValue) } : {}),
        // sortOrder = OKUMA sırası (satır → kolon): mobil tek-sütun render + tab sırası buna göre olur.
        fields: [...fields].sort((a, b) => a.rowStart - b.rowStart || a.colStart - b.colStart).map((f) => {
          const m = TYPE_META[f.type];
          const computed = m.number && !!f.formula;
          return {
            type: f.type,
            label: f.label.trim(),
            help: f.help.trim() || null,
            placeholder: m.value && !m.number ? (f.placeholder.trim() || null) : null,
            required: (m.value || m.table) && !computed ? f.required : false, // hesaplanan alan zorunlu olamaz; tablo "en az bir satır" zorunlusu korunur
            options: m.options ? f.options.map((o) => o.trim()).filter(Boolean) : [],
            minVal: m.number && !computed && f.minVal !== "" ? Number(f.minVal) : null,
            maxVal: m.number && !computed && f.maxVal !== "" ? Number(f.maxVal) : null,
            unit: m.number ? (f.unit.trim() || null) : null,
            colSpan: f.colSpan,
            colStart: f.colStart,
            rowStart: f.rowStart,
            rowSpan: (m.card || m.divider) ? f.rowSpan : 1,
            style: (m.divider || m.card) ? f.style : null,
            refKey: f.refKey,
            formula: computed ? f.formula : null,
            columns: m.table
              ? f.columns.map((c) => ({ key: c.key, label: c.label.trim(), type: c.type, options: c.type === "select" ? c.options.map((o) => o.trim()).filter(Boolean) : [], unit: c.type === "number" ? (c.unit?.trim() || null) : null, required: c.required }))
              : null,
          };
        }),
      };
      await saveMut.mutateAsync(payload);
      await utils.form.template.list.invalidate();
      if (isEdit) await utils.form.template.get.invalidate({ id: templateId! });
      onSaved(isEdit ? "Form güncellendi." : "Form oluşturuldu — yayınlamak için listeden \"Yayınla\"yı kullanın.");
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const statusBadge = status === "published"
    ? <span className="badge-pill b-success"><Icon name="check" size={11} />Yayında</span>
    : status === "archived"
      ? <span className="badge-pill"><Icon name="archive" size={11} />Arşiv</span>
      : <span className="badge-pill b-warn"><Icon name="edit" size={11} />Taslak</span>;

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Formu Düzenle" : "Yeni Form"}
      subtitle="Alanları ekleyip sürükleyerek düzenleyin · genişliği 12-kolon ızgarada ayarlayın"
      fullScreen
      dismissible={false}
      footer={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="button" className="btn-md secondary" onClick={() => setPreviewOpen(true)} disabled={fields.length === 0} title="Formu doldurma ekranında önizle — kaydedilmez">
              <Icon name="eye" size={14} />Önizleme
            </button>
            <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>{fields.length} alan · {statusBadge}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
            <button type="button" className="btn-md primary" onClick={save} disabled={saveMut.isPending}>
              <Icon name="check" size={14} />{saveMut.isPending ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      }
    >
      {isEdit && getQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : (
        <>
          {error ? <div className="form-banner-err" role="alert" style={{ marginBottom: 12 }}><Icon name="alertTriangle" size={14} />{error}</div> : null}

          {/* Üst: ad / açıklama / tesis */}
          <div className="fb-top">
            <div className="form-field">
              <label className="lbl" htmlFor="fb-name">Form Adı <span className="req">*</span></label>
              <input id="fb-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Vardiya Hijyen Kontrolü" maxLength={200} />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="fb-desc">Açıklama</label>
              <input id="fb-desc" className="form-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Formun amacı (opsiyonel)" maxLength={1000} />
            </div>
            {tesisPicker.show ? (
              <div className="full">
                <TesisPickerField show={tesisPicker.show} facilities={tesisPicker.facilities} value={tesisValue} onChange={setTesisValue} />
              </div>
            ) : null}
          </div>

          {/* Palet */}
          <div className="fb-palette">
            <span className="fb-palette-lbl">Alan Ekle</span>
            {PALETTE.map((t) => (
              <button key={t} type="button" className="fb-palette-btn" onClick={() => addField(t)}>
                <Icon name={TYPE_META[t].icon} size={13} />{TYPE_META[t].label}
              </button>
            ))}
          </div>

          {/* Kanvas + özellik paneli */}
          <div className="fb-main">
            <div className="fb-canvas-wrap">
              <div
                ref={canvasRef}
                className={`fb-canvas${dragKey ? " dragging" : ""}`}
                style={{ gridTemplateRows: `repeat(${gridRows}, ${BUILDER_ROW_H}px)`, gap: BUILDER_ROW_GAP }}
              >
                {/* 12 kolon × satır ızgarası — HER ZAMAN mount (koşulsuz); yalnızca .fb-canvas.dragging iken
                    pointer-events kazanıp bırakma hedefi olur. Böylece dragstart'ta DOM'a düğüm eklenmez ve
                    native drop güvenilir tetiklenir. */}
                <div className="fb-grid-overlay" style={{ gridTemplateRows: `repeat(${gridRows}, ${BUILDER_ROW_H}px)`, gap: BUILDER_ROW_GAP }} aria-hidden="true">
                  {Array.from({ length: gridRows * GRID_COLS }, (_, i) => {
                    const row = Math.floor(i / GRID_COLS), col = i % GRID_COLS;
                    const on = previewPos != null && dragField != null && row === previewPos.rowStart && col >= previewPos.colStart && col < previewPos.colStart + dragField.colSpan;
                    return (
                      <div key={i} className={`fb-grid-cell${on ? " on" : ""}`} />
                    );
                  })}
                </div>

                {fields.length === 0 && !dragKey ? (
                  <div className="fb-empty">
                    <Icon name="sheet" size={22} />
                    <span className="t">Henüz alan yok</span>
                    <span className="s">Yukarıdaki paletten alan ekleyin; ızgarada serbestçe sürükleyin.</span>
                  </div>
                ) : orderedFields.map((f) => {
                  const m = TYPE_META[f.type];
                  const vDivider = f.type === "divider" && f.style?.orientation === "v";
                  const spanRows = (m.card || vDivider) && f.rowSpan > 1;
                  const posStyle: React.CSSProperties = { gridColumn: `${f.colStart + 1} / span ${f.colSpan}`, gridRow: `${f.rowStart + 1}${spanRows ? ` / span ${f.rowSpan}` : ""}` };
                  const cls = `fb-cell${selectedKey === f.key ? " selected" : ""}${dragKey === f.key ? " dragging" : ""}${f.type === "heading" ? " heading-cell" : f.type === "note" ? " note-cell" : m.card ? " fb-card-cell" : m.divider ? " fb-divider-cell" : ""}`;
                  const dv = m.divider ? dividerStyles(f.style) : null;
                  const acts = (
                    <span className="fb-cell-acts">
                      <button type="button" className="fb-iact" aria-label="Yukarı taşı" title="Bir satır yukarı" disabled={f.rowStart === 0} onClick={(e) => { e.stopPropagation(); move(f.key, -1); }}><Icon name="chevronUp" size={14} /></button>
                      <button type="button" className="fb-iact" aria-label="Aşağı taşı" title="Bir satır aşağı" onClick={(e) => { e.stopPropagation(); move(f.key, 1); }}><Icon name="chevronDown" size={14} /></button>
                      <button type="button" className="fb-iact" aria-label="Çoğalt" title="Çoğalt (kopyala)" onClick={(e) => { e.stopPropagation(); duplicateField(f.key); }}><Icon name="copy" size={13} /></button>
                      <button type="button" className="fb-iact danger" aria-label="Sil" title="Sil" onClick={(e) => { e.stopPropagation(); removeField(f.key); }}><Icon name="trash" size={13} /></button>
                    </span>
                  );
                  return (
                    <div
                      key={f.key}
                      className={cls}
                      style={m.card ? { ...posStyle, ...cardStyles(f.style) } : posStyle}
                      tabIndex={0}
                      role="button"
                      aria-label={`${m.label}${f.label ? ": " + f.label : ""}. Satır ${f.rowStart + 1}, kolon ${f.colStart + 1}. Düzenlemek için seçin; Alt+ok ile taşıyın.`}
                      aria-pressed={selectedKey === f.key}
                      onMouseDown={(e) => beginDrag(e, f.key)}
                      onClick={() => setSelectedKey(f.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedKey(f.key); }
                        else if (e.altKey && e.key === "ArrowUp") { e.preventDefault(); move(f.key, -1); }
                        else if (e.altKey && e.key === "ArrowDown") { e.preventDefault(); move(f.key, 1); }
                        else if (e.altKey && e.key === "ArrowLeft") { e.preventDefault(); moveCol(f.key, -1); }
                        else if (e.altKey && e.key === "ArrowRight") { e.preventDefault(); moveCol(f.key, 1); }
                        else if (e.shiftKey && e.key === "ArrowLeft") { e.preventDefault(); setSpan(f.key, -1); }
                        else if (e.shiftKey && e.key === "ArrowRight") { e.preventDefault(); setSpan(f.key, 1); }
                        else if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) { e.preventDefault(); duplicateField(f.key); }
                        else if (e.key === "Delete") { e.preventDefault(); removeField(f.key); }
                      }}
                    >
                      {m.divider ? (
                        <div className="fb-divider-inner">
                          <span className="fb-cell-grip" aria-hidden="true"><Icon name="gripVertical" size={13} /></span>
                          <span className="fb-cell-type"><Icon name={m.icon} size={11} />Çizgi</span>
                          <div className="fb-divider-preview" style={dv!.wrap}><div style={dv!.line} /></div>
                          {acts}
                        </div>
                      ) : (
                        <div className="fb-cell-head">
                          <span className="fb-cell-grip" aria-hidden="true"><Icon name="gripVertical" size={14} /></span>
                          <span className="fb-cell-type"><Icon name={m.icon} size={11} />{m.label}</span>
                          {f.formula ? <span className="fb-cell-fx" title="Hesaplanan alan (formül)" aria-label="Hesaplanan alan">ƒ</span> : null}
                          {f.type === "table" ? <span className="fb-cell-fx" title="Tablo kolonları">{f.columns.length} kolon</span> : null}
                          {m.card ? <span className="fb-cell-fx" title="Grup kartı — içine denk gelen alanları gruplar">{f.colSpan}×{f.rowSpan}</span> : null}
                          <span className="fb-cell-label">{f.label || <span className="muted">{m.card ? "(başlıksız kart)" : "(etiketsiz)"}</span>}{(m.value || m.table) && !f.formula && f.required ? <span className="req"> *</span> : null}</span>
                          {!m.card ? <span className="fb-cell-span" title="Kolon genişliği (12 üzerinden)">{f.colSpan}/12</span> : null}
                          {acts}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="fb-canvas-hint">Alanları ızgarada serbestçe sürükleyip bırakın — kart içine, yan yana, alt alta ya da boşluk bırakarak. Klavye: <b>Alt+ok</b> taşı · <b>Shift+←/→</b> genişlik · <b>Ctrl+D</b> çoğalt · <b>Del</b> sil.</p>
            </div>

            {/* Özellik paneli */}
            <aside className="fb-props" aria-label="Alan özellikleri">
              {!selected ? (
                <>
                  <div className="fb-props-title"><Icon name="settings" size={13} />Alan Özellikleri</div>
                  <div className="fb-props-empty">Düzenlemek için bir alan seçin.</div>
                </>
              ) : (
                <>
                  <div className="fb-props-title"><Icon name={TYPE_META[selected.type].icon} size={13} />{TYPE_META[selected.type].label}{TYPE_META[selected.type].divider || TYPE_META[selected.type].card ? "" : " Alanı"}</div>
                  {TYPE_META[selected.type].divider ? null : (
                    <div className="form-field">
                      <label className="lbl" htmlFor="fp-label">{TYPE_META[selected.type].card ? "Başlık (opsiyonel)" : <>Etiket <span className="req">*</span></>}</label>
                      <input id="fp-label" className="form-input" value={selected.label} onChange={(e) => patch(selected.key, { label: e.target.value })} maxLength={200} placeholder={TYPE_META[selected.type].card ? "Grup başlığı" : undefined} />
                    </div>
                  )}
                  {TYPE_META[selected.type].divider || TYPE_META[selected.type].card ? null : (
                    <div className="form-field">
                      <label className="lbl" htmlFor="fp-help">Açıklama / Yardım</label>
                      <input id="fp-help" className="form-input" value={selected.help} onChange={(e) => patch(selected.key, { help: e.target.value })} placeholder="Alanın altında görünür" maxLength={500} />
                    </div>
                  )}

                  {TYPE_META[selected.type].value && !TYPE_META[selected.type].number ? (
                    <div className="form-field">
                      <label className="lbl" htmlFor="fp-ph">Placeholder</label>
                      <input id="fp-ph" className="form-input" value={selected.placeholder} onChange={(e) => patch(selected.key, { placeholder: e.target.value })} maxLength={200} />
                    </div>
                  ) : null}

                  {TYPE_META[selected.type].options ? (
                    <div className="form-field">
                      <label className="lbl" htmlFor="fp-opts">Seçenekler <span className="req">*</span> <span className="help" style={{ display: "inline" }}>(her satır bir seçenek)</span></label>
                      <textarea id="fp-opts" className="form-textarea" rows={4} value={selected.options.join("\n")} onChange={(e) => patch(selected.key, { options: e.target.value.split("\n") })} placeholder={"Uygun\nUygun Değil"} />
                    </div>
                  ) : null}

                  {TYPE_META[selected.type].number ? (
                    <>
                      <div className="form-field">
                        <ToggleSwitch
                          checked={!!selected.formula}
                          onChange={(v) => patch(selected.key, { formula: v ? { start: formulaCandidates[0] ? { kind: "field", ref: formulaCandidates[0].refKey } : { kind: "const", value: 0 }, steps: [] } : null })}
                          label="Hesaplanan alan (formül)"
                        />
                        <span className="help" style={{ display: "block", marginTop: 2 }}>Diğer alanların sıralı aritmetiğinden otomatik hesaplanır.</span>
                      </div>
                      {selected.formula ? (
                        <div className="form-field">
                          <label className="lbl">Formül</label>
                          <FormulaEditor candidates={formulaCandidates} value={selected.formula} onChange={(f) => patch(selected.key, { formula: f })} />
                        </div>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <div className="form-field">
                            <label className="lbl" htmlFor="fp-min">Min</label>
                            <input id="fp-min" className="form-input" type="number" step="any" value={selected.minVal} onChange={(e) => patch(selected.key, { minVal: e.target.value })} />
                          </div>
                          <div className="form-field">
                            <label className="lbl" htmlFor="fp-max">Max</label>
                            <input id="fp-max" className="form-input" type="number" step="any" value={selected.maxVal} onChange={(e) => patch(selected.key, { maxVal: e.target.value })} />
                          </div>
                        </div>
                      )}
                      <div className="form-field">
                        <label className="lbl" htmlFor="fp-unit">Birim</label>
                        <input id="fp-unit" className="form-input" value={selected.unit} onChange={(e) => patch(selected.key, { unit: e.target.value })} placeholder="°C, kg, %, ₺…" maxLength={30} />
                      </div>
                    </>
                  ) : null}

                  {TYPE_META[selected.type].value && !selected.formula ? (
                    <div className="form-field">
                      <ToggleSwitch checked={selected.required} onChange={(v) => patch(selected.key, { required: v })} label="Zorunlu alan" />
                    </div>
                  ) : null}

                  {TYPE_META[selected.type].table ? (
                    <>
                      <div className="form-field">
                        <label className="lbl">Kolonlar <span className="req">*</span></label>
                        <TableColumnsEditor value={selected.columns} onChange={(cols) => patch(selected.key, { columns: cols })} />
                      </div>
                      <div className="form-field">
                        <ToggleSwitch checked={selected.required} onChange={(v) => patch(selected.key, { required: v })} label="Zorunlu (en az bir satır)" />
                      </div>
                    </>
                  ) : null}

                  {TYPE_META[selected.type].divider ? (
                    <>
                      <div className="form-field">
                        <label className="lbl" htmlFor="fp-orient">Yön</label>
                        <select id="fp-orient" className="form-select" value={selected.style?.orientation ?? "h"} onChange={(e) => { const o = e.target.value as "h" | "v"; patchStyle(selected.key, { orientation: o }); if (o === "h") patch(selected.key, { rowSpan: 1 }); }}>
                          <option value="h">Yatay ▬</option>
                          <option value="v">Dikey ▮</option>
                        </select>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div className="form-field">
                          <label className="lbl" htmlFor="fp-thick">Kalınlık (px)</label>
                          <input id="fp-thick" className="form-input" type="number" min={1} max={20} value={selected.style?.thickness ?? 2} onChange={(e) => patchStyle(selected.key, { thickness: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })} />
                        </div>
                        <div className="form-field">
                          <label className="lbl" htmlFor="fp-dpad">Boşluk (px)</label>
                          <input id="fp-dpad" className="form-input" type="number" min={0} max={48} value={selected.style?.padding ?? 8} onChange={(e) => patchStyle(selected.key, { padding: Math.max(0, Math.min(48, Number(e.target.value) || 0)) })} />
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="lbl">Renk</label>
                        <div className="fb-color-row">
                          <input type="color" value={selected.style?.color ?? "#cbd5e1"} onChange={(e) => patchStyle(selected.key, { color: e.target.value })} aria-label="Çizgi rengi" />
                          <span className="fb-color-hex">{selected.style?.color ?? "#cbd5e1"}</span>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {TYPE_META[selected.type].card ? (
                    <>
                      <div className="form-field">
                        <label className="lbl" htmlFor="fp-cpad">İç boşluk (px)</label>
                        <input id="fp-cpad" className="form-input" type="number" min={0} max={48} value={selected.style?.padding ?? 12} onChange={(e) => patchStyle(selected.key, { padding: Math.max(0, Math.min(48, Number(e.target.value) || 0)) })} />
                      </div>
                      <div className="form-field">
                        <label className="lbl">Kenar rengi</label>
                        <div className="fb-color-row">
                          <input type="color" value={selected.style?.color ?? "#e2e8f0"} onChange={(e) => patchStyle(selected.key, { color: e.target.value })} aria-label="Kart kenar rengi" />
                          <span className="fb-color-hex">{selected.style?.color ?? "#e2e8f0"}</span>
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="lbl">Arka plan</label>
                        <div className="fb-color-row">
                          <input type="color" value={selected.style?.bg ?? "#f8fafc"} disabled={selected.style?.bg == null} onChange={(e) => patchStyle(selected.key, { bg: e.target.value })} aria-label="Kart arka planı" />
                          <label className="fb-col-req"><input type="checkbox" checked={selected.style?.bg == null} onChange={(e) => patchStyle(selected.key, { bg: e.target.checked ? null : "#f8fafc" })} />Şeffaf</label>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {/* Kolon genişliği */}
                  <div className="form-field">
                    <label className="lbl">Genişlik (kolon)</label>
                    <div className="fb-span-row">
                      <button type="button" className="fb-iact" aria-label="Genişliği azalt" onClick={() => setSpan(selected.key, -1)} disabled={selected.colSpan <= 1}><Icon name="chevronDown" size={14} /></button>
                      <input type="range" min={1} max={12} value={selected.colSpan} onChange={(e) => setSpanTo(selected.key, Number(e.target.value))} style={{ flex: 1 }} aria-label="Kolon genişliği" />
                      <button type="button" className="fb-iact" aria-label="Genişliği artır" onClick={() => setSpan(selected.key, 1)} disabled={selected.colSpan >= 12}><Icon name="chevronUp" size={14} /></button>
                      <span className="fb-span-val">{selected.colSpan}/12</span>
                    </div>
                  </div>

                  {TYPE_META[selected.type].card || (TYPE_META[selected.type].divider && selected.style?.orientation === "v") ? (
                    <div className="form-field">
                      <label className="lbl">Yükseklik (satır)</label>
                      <div className="fb-span-row">
                        <button type="button" className="fb-iact" aria-label="Yüksekliği azalt" onClick={() => setRowSpan(selected.key, -1)} disabled={selected.rowSpan <= 1}><Icon name="chevronDown" size={14} /></button>
                        <input type="range" min={1} max={12} value={Math.min(12, selected.rowSpan)} onChange={(e) => setRowSpanTo(selected.key, Number(e.target.value))} style={{ flex: 1 }} aria-label="Satır yüksekliği" />
                        <button type="button" className="fb-iact" aria-label="Yüksekliği artır" onClick={() => setRowSpan(selected.key, 1)}><Icon name="chevronUp" size={14} /></button>
                        <span className="fb-span-val">{selected.rowSpan} sat</span>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </aside>
          </div>
        </>
      )}
    </Drawer>
    <FormFillDrawer
      open={previewOpen}
      preview
      previewFields={previewFields}
      previewName={name || "(adsız form)"}
      previewDescription={description || null}
      templateId={null}
      submissionId={null}
      onClose={() => setPreviewOpen(false)}
      onSaved={() => {}}
    />
    </>
  );
}
